import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Printer } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1610891015188-5369212db097?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHx0ZXh0aWxlJTIwcHJpbnRpbmclMjBtYWNoaW5lJTIwZmFjdG9yeXxlbnwwfHx8fDE3ODUxNTU1MTd8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email.trim(), password);
      toast.success(`Welcome back, ${user.name}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = apiError(err.response?.data?.detail) || "Login failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex items-center justify-center bg-white px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-md bg-indigo-600 text-white">
              <Printer className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight text-slate-900">Nayara Digital Prints</h1>
              <p className="text-xs text-slate-500">ERP System</p>
            </div>
          </div>

          <h2 className="font-heading text-2xl font-bold text-slate-900">Sign in</h2>
          <p className="mt-1 text-sm text-slate-500">Enter your credentials to access the ERP.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@nayara.com" data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password" type={show ? "text" : "password"} required autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" data-testid="login-password-input" className="pr-10"
                />
                <button type="button" onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-btn" tabIndex={-1}>
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && <p className="text-sm text-rose-600" data-testid="login-error">{error}</p>}

            <Button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full bg-indigo-600 text-white transition-transform hover:-translate-y-[1px] hover:bg-indigo-700">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Sign in
            </Button>
          </form>

          <p className="mt-6 text-xs text-slate-400">Contact your administrator if you cannot access your account.</p>
        </div>
      </div>

      <div className="relative hidden lg:block">
        <img src={HERO} alt="Textile printing factory" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/70 to-slate-900/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h3 className="font-heading text-3xl font-black leading-tight">Enquiry to Delivery,<br />all in one place.</h3>
          <p className="mt-3 max-w-md text-sm text-slate-200">
            Manage quotations, artwork approvals, production, quality, inventory, dispatch and billing for your digital textile printing business.
          </p>
        </div>
      </div>
    </div>
  );
}
